/**
 * The "Install Velora" bottom sheet. Four variants by device (sheetMode):
 *
 *   prompt          Android/Chromium with a captured install prompt: Install / Not now
 *   ios-steps       iOS Safari: Share, then "Add to Home Screen", then Add
 *   open-in-safari  iOS Chrome / in-app browsers: open the site in Safari first
 *   browser-menu    a phone browser without a prompt: the browser menu's Install item
 */
import { PRODUCT } from '@velora/contracts/product';
import { BrandGlyph, Button, Dialog } from '@velora/ui';
import { Compass, Copy, EllipsisVertical, Share, SquarePlus } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import type { InstallPlatform } from './platform.js';
import './pwa.css';

export type SheetMode = 'prompt' | 'ios-steps' | 'open-in-safari' | 'browser-menu';

export function sheetMode(platform: InstallPlatform, hasPrompt: boolean): SheetMode {
  if (hasPrompt) return 'prompt';
  if (platform === 'ios-safari') return 'ios-steps';
  if (platform === 'ios-other') return 'open-in-safari';
  return 'browser-menu';
}

export interface InstallSheetProps {
  open: boolean;
  mode: SheetMode;
  /** "phone" or "tablet", for the title. */
  device: 'phone' | 'tablet' | 'device';
  onInstall: () => void;
  onNotNow: () => void;
  /** "Got it" on the instruction variants. */
  onAcknowledge: () => void;
}

function Step({ n, children }: { n: number; children: ReactNode }) {
  return (
    <li className="pwa-step">
      <span className="pwa-step__n" aria-hidden>
        {n}
      </span>
      <span className="pwa-step__text">{children}</span>
    </li>
  );
}

function Glyph({ children, label }: { children: ReactNode; label: string }) {
  return (
    <span className="pwa-glyph" role="img" aria-label={label}>
      {children}
    </span>
  );
}

function CopyLink() {
  const [copied, setCopied] = useState(false);
  // The site's front door only: the current path can name a patient.
  const link = `${window.location.origin}/`;
  return (
    <div className="pwa-copy">
      <code className="pwa-copy__url">{link}</code>
      <Button
        size="sm"
        onClick={() => {
          void navigator.clipboard
            ?.writeText(link)
            .then(() => setCopied(true))
            .catch(() => setCopied(false));
        }}
      >
        <Copy size={14} aria-hidden /> {copied ? 'Copied' : 'Copy link'}
      </Button>
    </div>
  );
}

function Body({ mode }: { mode: SheetMode }) {
  switch (mode) {
    case 'prompt':
      return null;
    case 'ios-steps':
      return (
        <ol className="pwa-steps" aria-label="How to install">
          <Step n={1}>
            Tap the Share button{' '}
            <Glyph label="Share">
              <Share size={18} aria-hidden />
            </Glyph>{' '}
            in Safari&apos;s toolbar.
          </Step>
          <Step n={2}>
            Scroll down and tap{' '}
            <strong className="pwa-chip">
              <SquarePlus size={16} aria-hidden /> Add to Home Screen
            </strong>
          </Step>
          <Step n={3}>
            Tap <strong>Add</strong>. {PRODUCT.shortName} appears on your home screen.
          </Step>
        </ol>
      );
    case 'open-in-safari':
      return (
        <>
          <p className="pwa-note">
            <Compass size={16} aria-hidden /> Only Safari can add {PRODUCT.shortName} to the home
            screen on iPhone and iPad.
          </p>
          <ol className="pwa-steps" aria-label="How to install">
            <Step n={1}>Copy this link and open it in Safari.</Step>
            <Step n={2}>
              In Safari, tap Share{' '}
              <Glyph label="Share">
                <Share size={18} aria-hidden />
              </Glyph>{' '}
              then <strong>Add to Home Screen</strong>.
            </Step>
          </ol>
          <CopyLink />
        </>
      );
    case 'browser-menu':
      return (
        <ol className="pwa-steps" aria-label="How to install">
          <Step n={1}>
            Open the browser menu{' '}
            <Glyph label="Menu">
              <EllipsisVertical size={18} aria-hidden />
            </Glyph>
          </Step>
          <Step n={2}>
            Tap <strong>Install app</strong> or <strong>Add to Home screen</strong>.
          </Step>
        </ol>
      );
  }
}

export function InstallSheet({
  open,
  mode,
  device,
  onInstall,
  onNotNow,
  onAcknowledge,
}: InstallSheetProps) {
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onNotNow();
      }}
      size="sm"
      className="pwa-sheet"
      title={
        <span className="pwa-sheet__title">
          <BrandGlyph size={36} className="pwa-sheet__mark" />
          <span>
            Install {PRODUCT.shortName} on this {device}
          </span>
        </span>
      }
      description="Opens like an app, faster, full screen."
      footer={
        <div className="pwa-sheet__actions">
          <Button variant="ghost" onClick={onNotNow}>
            Not now
          </Button>
          {mode === 'prompt' ? (
            <Button variant="primary" onClick={onInstall}>
              Install
            </Button>
          ) : (
            <Button variant="primary" onClick={onAcknowledge}>
              Got it
            </Button>
          )}
        </div>
      }
    >
      <Body mode={mode} />
    </Dialog>
  );
}
